import { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import {
  Users, BarChart3, Mail, Shield, Plus, Trash2, UserCog,
  LogOut, ArrowLeft, Loader2, CheckCircle2, XCircle,
  Activity, Send, Clock, Eye, KeyRound, ToggleLeft, ToggleRight,
} from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

export function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [selectedUserLogs, setSelectedUserLogs] = useState(null);
  const [userLogs, setUserLogs] = useState([]);
  const [passwordDialog, setPasswordDialog] = useState({ open: false, userId: null, username: "" });

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get("/admin/stats");
      setStats(res.data);
    } catch {
      toast.error("Erreur chargement statistiques");
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch {
      toast.error("Erreur chargement utilisateurs");
    }
  }, []);

  useEffect(() => {
    Promise.all([loadStats(), loadUsers()]).finally(() => setLoading(false));
  }, [loadStats, loadUsers]);

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Supprimer l'utilisateur "${username}" ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("Utilisateur supprimé");
      loadUsers();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await api.put(`/admin/users/${userId}`, { is_active: !isActive });
      toast.success(isActive ? "Compte désactivé" : "Compte activé");
      loadUsers();
    } catch {
      toast.error("Erreur");
    }
  };

  const handleResetPassword = async (userId, username) => {
    setPasswordDialog({ open: true, userId, username });
  };

  const handleViewLogs = async (userId, username) => {
    setSelectedUserLogs(username);
    try {
      const res = await api.get(`/admin/users/${userId}/logs`);
      setUserLogs(res.data);
    } catch {
      toast.error("Erreur chargement logs");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Vue d'ensemble", icon: BarChart3 },
    { id: "users", label: "Utilisateurs", icon: Users },
    { id: "activity", label: "Activité", icon: Activity },
    { id: "emails", label: "Adresses SMTP", icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-zinc-900 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-zinc-900 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Administration
                </h1>
                <p className="text-[10px] text-zinc-400 uppercase tracking-widest font-medium">
                  netlinkMailing
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-xs">
              <UserCog className="w-3 h-3 mr-1" />
              {user?.full_name || user?.username}
            </Badge>
            <Button variant="ghost" size="sm" className="gap-2 text-zinc-500" onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4" />
              App
            </Button>
            <Button variant="ghost" size="sm" className="gap-2 text-zinc-500" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-zinc-200 bg-white">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-zinc-500 hover:text-zinc-700"
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          {activeTab === "overview" && (
            <OverviewTab key="overview" stats={stats} />
          )}
          {activeTab === "users" && (
            <UsersTab
              key="users"
              users={users}
              showCreateUser={showCreateUser}
              setShowCreateUser={setShowCreateUser}
              onDelete={handleDeleteUser}
              onToggleActive={handleToggleActive}
              onResetPassword={handleResetPassword}
              onViewLogs={handleViewLogs}
              onRefresh={() => { loadUsers(); loadStats(); }}
            />
          )}
          {activeTab === "activity" && (
            <ActivityTab key="activity" stats={stats} selectedUserLogs={selectedUserLogs} userLogs={userLogs} />
          )}
          {activeTab === "emails" && (
            <EmailsTab key="emails" stats={stats} />
          )}
        </AnimatePresence>
      </main>

      {/* Password Change Dialog */}
      <SetPasswordDialog
        open={passwordDialog.open}
        onOpenChange={(open) => setPasswordDialog({ ...passwordDialog, open })}
        userId={passwordDialog.userId}
        username={passwordDialog.username}
      />
    </div>
  );
}


// ─── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab({ stats }) {
  if (!stats) return null;
  const { overview } = stats;

  const cards = [
    { label: "Utilisateurs", value: overview.total_users, icon: Users, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Actifs", value: overview.active_users, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Campagnes", value: overview.total_sends, icon: Send, color: "text-purple-600", bg: "bg-purple-50" },
    { label: "Emails envoyés", value: overview.total_emails, icon: Mail, color: "text-primary", bg: "bg-red-50" },
    { label: "Réussis", value: overview.total_success, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="space-y-8"
    >
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map((card) => (
          <div key={card.label} className="p-5 rounded-xl border border-zinc-200 bg-white">
            <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center mb-3`}>
              <card.icon className={`w-4.5 h-4.5 ${card.color}`} />
            </div>
            <p className="text-2xl font-black text-zinc-900">{card.value.toLocaleString()}</p>
            <p className="text-xs text-zinc-500 mt-1 uppercase tracking-wide font-medium">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Per-user stats */}
      {stats.user_stats.length > 0 && (
        <div className="border border-zinc-200 rounded-xl bg-white overflow-hidden">
          <div className="p-5 border-b border-zinc-100">
            <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-500">
              Statistiques par utilisateur
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-zinc-50">
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-zinc-500 py-3 px-5">Utilisateur</th>
                  <th className="text-right text-xs font-bold uppercase tracking-wider text-zinc-500 py-3 px-5">Campagnes</th>
                  <th className="text-right text-xs font-bold uppercase tracking-wider text-zinc-500 py-3 px-5">Emails</th>
                  <th className="text-right text-xs font-bold uppercase tracking-wider text-zinc-500 py-3 px-5">Réussis</th>
                  <th className="text-right text-xs font-bold uppercase tracking-wider text-zinc-500 py-3 px-5">Échoués</th>
                  <th className="text-right text-xs font-bold uppercase tracking-wider text-zinc-500 py-3 px-5">Taux</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {stats.user_stats.map((u) => (
                  <tr key={u.id} className="hover:bg-zinc-50/50">
                    <td className="py-3 px-5">
                      <p className="text-sm font-medium text-zinc-900">{u.full_name || u.username}</p>
                      <p className="text-xs text-zinc-400">@{u.username}</p>
                    </td>
                    <td className="py-3 px-5 text-right text-sm font-mono text-zinc-700">{u.send_count}</td>
                    <td className="py-3 px-5 text-right text-sm font-mono text-zinc-700">{u.total_emails}</td>
                    <td className="py-3 px-5 text-right text-sm font-mono text-emerald-600">{u.success_emails}</td>
                    <td className="py-3 px-5 text-right text-sm font-mono text-red-600">{u.failed_emails}</td>
                    <td className="py-3 px-5 text-right">
                      <Badge variant={u.total_emails > 0 && u.success_emails / u.total_emails > 0.9 ? "default" : "secondary"} className="text-xs font-mono">
                        {u.total_emails > 0 ? Math.round((u.success_emails / u.total_emails) * 100) : 0}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </motion.div>
  );
}


// ─── Users Tab ────────────────────────────────────────────────────────────────

function UsersTab({ users, showCreateUser, setShowCreateUser, onDelete, onToggleActive, onResetPassword, onViewLogs, onRefresh }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-500">
          {users.length} utilisateur{users.length > 1 ? "s" : ""}
        </h3>
        <CreateUserSheet open={showCreateUser} onOpenChange={setShowCreateUser} onCreated={onRefresh} />
      </div>

      <div className="grid gap-3">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-4 p-4 border border-zinc-200 rounded-xl bg-white hover:shadow-sm transition-shadow">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
              u.role === "admin" ? "bg-zinc-900 text-white" : "bg-primary/10 text-primary"
            }`}>
              {(u.full_name || u.username).charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-zinc-900">{u.full_name || u.username}</p>
                <Badge variant={u.role === "admin" ? "default" : "secondary"} className="text-[10px]">
                  {u.role === "admin" ? "Admin" : "Utilisateur"}
                </Badge>
                {!u.is_active && (
                  <Badge variant="destructive" className="text-[10px]">Désactivé</Badge>
                )}
              </div>
              <p className="text-xs text-zinc-400">@{u.username} • {u.email}</p>
              <p className="text-[10px] text-zinc-400 mt-0.5">
                Créé le {new Date(u.created_at).toLocaleDateString("fr-FR")}
                {u.last_login && ` • Dernière connexion: ${new Date(u.last_login).toLocaleDateString("fr-FR")}`}
              </p>
            </div>
            {u.role !== "admin" && (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" title="Voir les logs" onClick={() => onViewLogs(u.id, u.username)}>
                  <Eye className="w-3.5 h-3.5 text-zinc-400" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" title="Réinitialiser mot de passe" onClick={() => onResetPassword(u.id, u.username)}>
                  <KeyRound className="w-3.5 h-3.5 text-zinc-400" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" title={u.is_active ? "Désactiver" : "Activer"} onClick={() => onToggleActive(u.id, u.is_active)}>
                  {u.is_active ? <ToggleRight className="w-3.5 h-3.5 text-emerald-500" /> : <ToggleLeft className="w-3.5 h-3.5 text-zinc-400" />}
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-red-500" title="Supprimer" onClick={() => onDelete(u.id, u.username)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
}


// ─── Create User Sheet ────────────────────────────────────────────────────────

function CreateUserSheet({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ username: "", email: "", password: "", full_name: "", role: "user" });
  const [creating, setCreating] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.username || !form.email || !form.password) {
      toast.error("Remplissez tous les champs obligatoires");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`Utilisateur "${form.username}" créé`);
      setForm({ username: "", email: "", password: "", full_name: "", role: "user" });
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de création");
    }
    setCreating(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button className="gap-2 rounded-full" size="sm" data-testid="create-user-button">
          <Plus className="w-4 h-4" />
          Créer un utilisateur
        </Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            Nouvel utilisateur
          </SheetTitle>
          <SheetDescription>
            Créez un compte pour un nouveau membre de l'équipe
          </SheetDescription>
        </SheetHeader>
        <form onSubmit={handleCreate} className="space-y-5 mt-6">
          <div className="space-y-2">
            <Label className="text-sm">Nom complet</Label>
            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Jean Dupont" data-testid="create-fullname" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Nom d'utilisateur *</Label>
            <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="jean.dupont" required data-testid="create-username" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Email *</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jean@exemple.com" required data-testid="create-email" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Mot de passe *</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimum 6 caractères" required data-testid="create-password" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Rôle</Label>
            <div className="flex gap-2">
              <Button type="button" variant={form.role === "user" ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setForm({ ...form, role: "user" })}>
                Utilisateur
              </Button>
              <Button type="button" variant={form.role === "admin" ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setForm({ ...form, role: "admin" })}>
                Administrateur
              </Button>
            </div>
          </div>
          <Button type="submit" className="w-full rounded-full gap-2" disabled={creating} data-testid="submit-create-user">
            {creating && <Loader2 className="w-4 h-4 animate-spin" />}
            Créer le compte
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}


// ─── Activity Tab ─────────────────────────────────────────────────────────────

function ActivityTab({ stats, selectedUserLogs, userLogs }) {
  const logs = selectedUserLogs ? userLogs : (stats?.recent_logs || []);
  const title = selectedUserLogs ? `Activité de @${selectedUserLogs}` : "Activité récente (tous les utilisateurs)";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-500">{title}</h3>

      {logs.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Aucune activité enregistrée</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => (
            <div key={log.id} className="p-4 border border-zinc-200 rounded-xl bg-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Send className="w-3.5 h-3.5 text-primary" />
                    <p className="text-sm font-medium text-zinc-900 truncate max-w-[300px]">{log.subject}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-zinc-500">
                    {log.username && <span>@{log.username}</span>}
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {log.sender_email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(log.created_at).toLocaleString("fr-FR")}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Badge variant="secondary" className="text-xs font-mono gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    {log.success_count}
                  </Badge>
                  {log.failed_count > 0 && (
                    <Badge variant="secondary" className="text-xs font-mono gap-1">
                      <XCircle className="w-3 h-3 text-red-500" />
                      {log.failed_count}
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-xs font-mono">
                    {log.total_recipients} dest.
                  </Badge>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}


// ─── Emails Tab ───────────────────────────────────────────────────────────────

function EmailsTab({ stats }) {
  const emails = stats?.sender_emails || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-500">
        Adresses SMTP utilisées
      </h3>

      {emails.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Aucune adresse utilisée</p>
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-xl bg-white overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50">
                <th className="text-left text-xs font-bold uppercase tracking-wider text-zinc-500 py-3 px-5">Adresse email</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-zinc-500 py-3 px-5">Utilisateur</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-zinc-500 py-3 px-5">Utilisations</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {emails.map((e, idx) => (
                <tr key={idx} className="hover:bg-zinc-50/50">
                  <td className="py-3 px-5">
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-zinc-400" />
                      <span className="text-sm font-mono text-zinc-700">{e.sender_email}</span>
                    </div>
                  </td>
                  <td className="py-3 px-5">
                    <span className="text-sm text-zinc-600">{e.full_name || e.username}</span>
                    <span className="text-xs text-zinc-400 ml-2">@{e.username}</span>
                  </td>
                  <td className="py-3 px-5 text-right">
                    <Badge variant="secondary" className="text-xs font-mono">{e.times_used}x</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}


// ─── Set Password Dialog ──────────────────────────────────────────────────────

function SetPasswordDialog({ open, onOpenChange, userId, username }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 4) {
      toast.error("Le mot de passe doit contenir au moins 4 caractères");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/users/${userId}/set-password`, { new_password: newPassword });
      toast.success(`Mot de passe de @${username} modifié avec succès`);
      setNewPassword("");
      setConfirmPassword("");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-primary" />
            Modifier le mot de passe
          </DialogTitle>
          <DialogDescription>
            Définir un nouveau mot de passe pour <strong>@{username}</strong>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-sm">Nouveau mot de passe</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 4 caractères"
              autoFocus
              data-testid="admin-new-password"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Confirmer le mot de passe</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Retapez le mot de passe"
              data-testid="admin-confirm-password"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" className="flex-1 rounded-full gap-2" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Enregistrer
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
